// src/components/Sections/Gallery.jsx (aktualisiert mit galleryLoader)

import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { GalleryCard } from '../UI/GalleryCard';
import { Lightbox } from '../Layout/Lightbox';
import { 
  loadArtworks, 
  filterByCategory, 
  searchArtworks,
  sortArtworks,
  prepareArtworks 
} from '../../utils/galleryLoader';

export const Gallery = () => {
  const { t } = useLanguage();
  const [artworks, setArtworks] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter States
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('title');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await loadArtworks();
        const prepared = prepareArtworks(data);
        setArtworks(prepared);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Gefilterte & Sortierte Kunstwerke
  const filteredArtworks = useMemo(() => {
    let result = [...artworks];
    result = filterByCategory(result, categoryFilter);
    result = searchArtworks(result, searchQuery);
    result = sortArtworks(result, sortBy);
    return result;
  }, [artworks, categoryFilter, searchQuery, sortBy]);

  // Einzigartige Kategorien für Filter
  const categories = useMemo(() => {
    const cats = [...new Set(artworks.map(a => a.category))];
    return ['all', ...cats];
  }, [artworks]);

  const selectedArtwork = selectedIndex !== null ? filteredArtworks[selectedIndex] : null;

  const handleNext = () => {
    setSelectedIndex((prev) => (prev + 1) % filteredArtworks.length);
  };

  const handlePrev = () => {
    setSelectedIndex((prev) => (prev - 1 + filteredArtworks.length) % filteredArtworks.length);
  };

  if (loading) {
    return (
      <section id="gallery" className="flex items-center justify-center min-h-screen">
        <div className="text-[var(--accent)] text-xl animate-pulse">
          Loading Gallery...
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="gallery" className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-red-500">
          Error: {error}
        </div>
      </section>
    );
  }

  return (
    <section
      id="gallery"
      className="min-h-screen px-6 py-20 room"
      role="region"
      aria-label="Gallery"
    >
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="text-[var(--accent)] text-sm font-semibold tracking-widest uppercase mb-4">
            {t('galleryMeta')}
          </div>
          <h2 className="mb-4 text-4xl font-bold font-syne md:text-5xl">
            {t('galleryHeading')}
          </h2>
          <p className="text-[var(--muted)] text-lg max-w-2xl mb-8">
            {t('galleryDesc')}
          </p>

          {/* Filter & Search */}
          <div className="p-4 space-y-4 glass rounded-xl">
            {/* Category Filter */}
            <div className="flex gap-2 pb-2 overflow-x-auto no-scrollbar">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    categoryFilter === cat
                      ? 'bg-[var(--accent)] text-[var(--bg)]'
                      : 'bg-[var(--card)] hover:bg-[var(--border)]'
                  }`}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="
                  w-full min-w-0
                  px-4 py-2
                  rounded-full
                  bg-[var(--card)]
                  border border-[var(--border)]
                  text-[var(--text)]
                  placeholder-[var(--muted)]
                  focus:outline-none
                  focus:border-[var(--accent)]
                "
              />
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="
                  w-full sm:w-auto
                  min-w-0
                  px-4 py-2
                  rounded-full
                  bg-[var(--card)]
                  border border-[var(--border)]
                  text-[var(--text)]
                  focus:outline-none
                  focus:border-[var(--accent)]
                  cursor-pointer
                "
              >
                <option value="title">Title</option>
                <option value="artist">Artist</option>
                <option value="date">Date</option>
                <option value="random">Random</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-[var(--muted)]">
            Showing {filteredArtworks.length} of {artworks.length} works
          </div>
        </div>

        {/* Gallery Grid */}
        {filteredArtworks.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredArtworks.map((artwork, index) => (
              <GalleryCard
                key={artwork.id}
                artwork={artwork}
                onClick={() => setSelectedIndex(index)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-[var(--muted)]">
            No artworks found matching your criteria.
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Lightbox
        artwork={selectedArtwork}
        onClose={() => setSelectedIndex(null)}
        onNext={handleNext}
        onPrev={handlePrev}
      />
    </section>
  );
};